import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { FavoriteFields } from "@graylog/server-api";
import { StreamsActions } from "views/stores/StreamsStore";
import UserNotification from "util/UserNotification";
import useSendFavoriteFieldTelemetry from "views/components/messagelist/MessageFields/hooks/useSendFavoriteFieldTelemetry";
const useMessageFavoriteFieldsMutation = (streams, initialFavoriteFields) => {
  const sendFavoriteFieldTelemetry = useSendFavoriteFieldTelemetry();
  const { isPending: setFieldsIsPending, mutate: setFavoriteFields } = useMutation({
    mutationFn: (props) => FavoriteFields.set(props),
    onSuccess: (_, newFavoriteFieldsByStream) => {
      sendFavoriteFieldTelemetry("EDIT_SAVED", {
        fields_lengths: Object.values(newFavoriteFieldsByStream.fields).map((fields) => fields.length)
      });
      return StreamsActions.refresh();
    },
    onError: (errorThrown) => UserNotification.error(
      `Setting fields to favorites failed with error: ${errorThrown}`,
      "Could not set fields to favorites"
    )
  });
  const { mutate: addFavoriteField } = useMutation({
    mutationFn: (props) => FavoriteFields.add(props),
    onSuccess: () => {
      sendFavoriteFieldTelemetry("TOGGLED", {
        app_action_value: "add"
      });
      return StreamsActions.refresh();
    },
    onError: (errorThrown) => UserNotification.error(
      `Adding field to favorites failed with error: ${errorThrown}`,
      "Could not add field to favorites"
    )
  });
  const { mutate: removeFavoriteField } = useMutation({
    mutationFn: (props) => FavoriteFields.remove(props),
    onSuccess: () => {
      sendFavoriteFieldTelemetry("TOGGLED", {
        app_action_value: "remove"
      });
      return StreamsActions.refresh();
    },
    onError: (errorThrown) => UserNotification.error(
      `Removing field from favorites failed with error: ${errorThrown}`,
      "Could not remove field from favorites"
    )
  });
  const saveFavoriteField = useCallback(
    (favoritesToSave) => {
      const newAddedFields = favoritesToSave.filter((f) => !initialFavoriteFields.includes(f));
      const newFavoriteFieldsByStream = Object.fromEntries(
        streams.map((stream) => [
          stream.id,
          favoritesToSave.filter((f) => {
            var _a;
            const streamFavoriteFields = (_a = stream == null ? void 0 : stream.favorite_fields) != null ? _a : [];
            return streamFavoriteFields.includes(f) || newAddedFields.includes(f);
          })
        ])
      );
      setFavoriteFields({ fields: newFavoriteFieldsByStream });
    },
    [initialFavoriteFields, setFavoriteFields, streams]
  );
  const toggleField = useCallback(
    (field) => {
      const isFavorite = initialFavoriteFields == null ? void 0 : initialFavoriteFields.includes(field);
      const streamIds = streams.map((stream) => stream.id);
      if (isFavorite) {
        removeFavoriteField({ field, stream_ids: streamIds });
      } else {
        addFavoriteField({ field, stream_ids: streamIds });
      }
    },
    [addFavoriteField, initialFavoriteFields, removeFavoriteField, streams]
  );
  return {
    setFieldsIsPending,
    saveFavoriteField,
    toggleField
  };
};
export default useMessageFavoriteFieldsMutation;
